import {
  CountPssByBookingIdQuery,
  CountPssByBookingIdQueryVariables,
  GetAccountingEntiesByListOfAccountingIdsQuery,
  GetAccountingEntiesByListOfAccountingIdsQueryVariables,
  GetAccountingWithUuidQuery,
  GetAccountingWithUuidQueryVariables,
  GetBookingAccountingsQuery,
  GetBookingAccountingsQueryVariables,
  GetBookingInfoQuery,
  GetBookingInfoQueryVariables,
  GetBookingWithUuidQuery,
  GetBookingWithUuidQueryVariables,
  GetFullBookingByIdQuery,
  GetFullBookingByIdQueryVariables,
  GetOtherTypesAccountingsByBookingIdQuery,
  GetOtherTypesAccountingsByBookingIdQueryVariables,
  GetPssByBookingIdQuery,
  GetPssByBookingIdQueryVariables,
  GetTicketTypeAccountingsByBookingIdWithAccEntriesQuery,
  GetTicketTypeAccountingsByBookingIdWithAccEntriesQueryVariables,
} from "@/types/gql/generated/graphql";
import { apolloClient } from "@/utils/graphql-client";
import {
  GET_AccountingWithUUID,
  GET_BookingAccountings,
  GET_BookingWithUUID,
  GET_FullBookingById,
} from "./queries/product-slot-booking.graphql";
import {
  COUNT_PSSByBookingId,
  Get_AccountingEntiesByListOfAccountingIds,
  GET_BookingInfo,
  GET_OtherTypesAccountingsByBookingId,
  GET_PSSByBookingId,
  GET_TicketTypeAccountingsByBookingIdWithAccEntries,
} from "./queries/orders.graphql";
import { IPaginate } from "@/types/app";

const EDIT_ORDER_QUERY_LIMIT = 300;

/**
 * Returns Booking using uuid.
 *
 * @param uid booking uuid
 */
export async function getBookingWithUUID(uid: string) {
  const res = await apolloClient.query<
    GetBookingWithUuidQuery,
    GetBookingWithUuidQueryVariables
  >({
    query: GET_BookingWithUUID,
    variables: { uuid: `(uuid,eq,${uid})` },
  });

  const bookings = res.data.booking;
  return bookings && bookings.length ? bookings[0] : null;
}

/**
 * Returns Booking using id.
 *
 * @param id booking id
 */
export async function getFullBookingById(id: string) {
  const res = await apolloClient.query<
    GetFullBookingByIdQuery,
    GetFullBookingByIdQueryVariables
  >({
    query: GET_FullBookingById,
    variables: { id: `(id,eq,${id})` },
  });

  const bookings = res.data.booking;
  return bookings && bookings.length ? bookings[0] : null;
}

/**
 * Returns Booking accountings using id.
 *
 * @param id booking id
 */
export async function getBookingAccountings(bookingId: string) {
  const res = await apolloClient.query<
    GetBookingAccountingsQuery,
    GetBookingAccountingsQueryVariables
  >({
    query: GET_BookingAccountings,
    variables: { bookingId: `(booking_id,eq,${bookingId})` },
  });

  const accountings = res.data.accountings;
  return accountings;
}

/**
 * Returns accounting using uuid.
 *
 * @param uid accounting uuid
 */
export async function getAccountingWithUUID(uid: string) {
  const res = await apolloClient.query({
    query: GET_AccountingWithUUID,
    variables: { uuid: `(uuid,eq,${uid})` },
  });

  const accountings = res.data.accounting;
  return accountings && accountings.length ? accountings[0] : null;
}

//////////////////////////////////////// LARGE BOOKINGS PULLINGS FUNCTIONS

/**
 *
 * Pull Booking object with top level fields only
 */
export async function getBookingInfo(bookingId: string) {
  const res = await apolloClient.query<
    GetBookingInfoQuery,
    GetBookingInfoQueryVariables
  >({
    query: GET_BookingInfo,
    variables: { id: `(id,eq,${bookingId})` },
  });

  console.log("res");
  console.log(res);

  const bookings = res.data.getBookingInfoById;
  return bookings && bookings.length ? bookings[0] : null;
}

/**
 * GetPSSBookingId: Pull PSS list of this booking wtih a paginated query
 */
export async function getPSSBookingId(bookingId: string) {
  const limit = EDIT_ORDER_QUERY_LIMIT; // Define the maximum number of records per page
  let skip = 0; // Start with the first page
  let allPSS: any[] = [];

  // eslint-disable-next-line
  while (true) {
    const variables: any = {
      id: `(booking_id,eq,${bookingId})`,
      skip,
      limit,
    };

    const res = await apolloClient.query<
      GetPssByBookingIdQuery,
      GetPssByBookingIdQueryVariables
    >({
      query: GET_PSSByBookingId,
      variables,
    });

    const currentPSS = res.data.getPSSBookingId || [];
    allPSS = allPSS.concat(currentPSS);

    // If the number of records fetched is less than the limit, we have fetched all records
    if (currentPSS.length < limit) {
      break;
    }

    // Move to the next page
    skip += limit;
  }

  return allPSS;
}

/**
 * countPSSByBookingId: count PSS list of this booking
 */
export async function countPSSByBookingId(bookingId: string) {
  const variables: any = { id: `(booking_id,eq,${bookingId})` };

  const res = await apolloClient.query<
    CountPssByBookingIdQuery,
    CountPssByBookingIdQueryVariables
  >({
    query: COUNT_PSSByBookingId,
    variables,
  });

  console.log("res");
  console.log(res);

  return res.data.countPSSByBookingId;
}

/**
 * Pull accountings of type TICKET of this booking wtih a paginated query, with their accountingEntries array
 */
export async function getTicketTypeAccountingsByBookingIdWithAccEntries_Paginated(
  bookingId: string
) {
  const limit = EDIT_ORDER_QUERY_LIMIT; // Define the maximum number of records per page
  let skip = 0; // Start with the first page
  let allAccountings: any[] = [];

  // eslint-disable-next-line
  while (true) {
    const variables: any = {
      cond: `(booking_id,eq,${bookingId})~and(transaction_type,eq,4)`,
      skip,
      limit,
    };

    const res = await apolloClient.query<
      GetTicketTypeAccountingsByBookingIdWithAccEntriesQuery,
      GetTicketTypeAccountingsByBookingIdWithAccEntriesQueryVariables
    >({
      query: GET_TicketTypeAccountingsByBookingIdWithAccEntries,
      variables,
    });

    const currentAccountings =
      res.data.GetTicketTypeAccountingsByBookingIdWithAccEntries || [];
    allAccountings = allAccountings.concat(currentAccountings);

    // If the number of records fetched is less than the limit, we have fetched all records
    if (currentAccountings.length < limit) {
      break;
    }

    // Move to the next page
    skip += limit;
  }

  return allAccountings;
}

/**
 * GetOtherTypesAccountingsByBookingId: Pull each accounting of type NOT TICKET (so discount and payment or any other type) of this booking
 */
export async function getOtherTypesAccountingsByBookingId(bookingId: string) {
  const variables: any = {
    cond: `(booking_id,eq,${bookingId})~and((transaction_type,lt,4)~or(transaction_type,gt,4))`,
  };

  const res = await apolloClient.query<
    GetOtherTypesAccountingsByBookingIdQuery,
    GetOtherTypesAccountingsByBookingIdQueryVariables
  >({
    query: GET_OtherTypesAccountingsByBookingId,
    variables,
  });

  console.log("res");
  console.log(res);

  return res.data.getOtherTypesAccountingsByBookingId;
}

/**
 * GetAccountingEntiesByListOfAccountingIds_Paginated: Pull accountingEntries of accountings of GetOtherTypesAccountingsByBookingId with a paginated query
 */
export async function getAccountingEntiesByListOfAccountingIds_Paginated(
  accountingIds: string[]
) {
  const limit = EDIT_ORDER_QUERY_LIMIT; // Define the maximum number of records per page
  let skip = 0; // Start with the first page
  let allAccountingEntries: any[] = [];

  // eslint-disable-next-line
  while (true) {
    const variables: any = {
      cond: `(accounting_id,in,${accountingIds.join(",")})`,
      skip,
      limit,
    };

    const res = await apolloClient.query<
      GetAccountingEntiesByListOfAccountingIdsQuery,
      GetAccountingEntiesByListOfAccountingIdsQueryVariables
    >({
      query: Get_AccountingEntiesByListOfAccountingIds,
      variables,
    });

    const currentAccountingEntries =
      res.data.GetAccountingEntiesByListOfAccountingIds || [];
    allAccountingEntries = allAccountingEntries.concat(
      currentAccountingEntries
    );

    // If the number of records fetched is less than the limit, we have fetched all records
    if (currentAccountingEntries.length < limit) {
      break;
    }

    // Move to the next page
    skip += limit;
  }

  return allAccountingEntries;
}
